import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { updateSocietyPricing } from "../api";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import {
  fetchSocieties,
  updateSocietyInStore,
} from "../store/slices/societySlice";

const PricingConfiguration = ({ society, onSave, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { theme, gradient, activeSociety, setActiveSociety } =
    useSidebarContext();
  const [pricing, setPricing] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [newMarlaSize, setNewMarlaSize] = useState("");

  const categories = ["General", "Park Face", "Corner", "Boulevard"];
  const defaultMarlaSizes = ["5", "10", "20", "25", "50"];

  const getAllMarlaSizes = () => {
    const allSizes = new Set(defaultMarlaSizes);
    categories.forEach((category) => {
      if (pricing[category]) {
        Object.keys(pricing[category]).forEach((size) => {
          allSizes.add(size);
        });
      }
    });
    return Array.from(allSizes)
      .map(Number)
      .sort((a, b) => a - b)
      .map(String);
  };

  const marlaSizes = getAllMarlaSizes();

  useEffect(() => {
    if (society?.plot_pricing) {
      setPricing(society.plot_pricing);
    } else {
      const defaultPricing = {};
      categories.forEach((category) => {
        defaultPricing[category] = {};
        defaultMarlaSizes.forEach((size) => {
          defaultPricing[category][size] = {
            price_per_marla: 0,
            description: `${size} Marla ${category}`,
          };
        });
      });
      setPricing(defaultPricing);
    }
  }, [society]);

  const updatePricing = (category, size, value) => {
    setPricing((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [size]: {
          ...prev[category]?.[size],
          price_per_marla: parseFloat(value) || 0,
        },
      },
    }));
  };

  const addMarlaSize = () => {
    if (!newMarlaSize || isNaN(newMarlaSize) || parseFloat(newMarlaSize) <= 0) {
      enqueueSnackbar("Please enter a valid marla size", { variant: "error" });
      return;
    }

    const size = newMarlaSize.toString();
    if (marlaSizes.includes(size)) {
      enqueueSnackbar("This marla size already exists", { variant: "warning" });
      return;
    }

    setPricing((prev) => {
      const updated = { ...prev };
      categories.forEach((category) => {
        if (!updated[category]) updated[category] = {};
        updated[category][size] = {
          price_per_marla: 0,
          description: `${size} Marla ${category}`,
        };
      });
      return updated;
    });

    setNewMarlaSize("");
    enqueueSnackbar(`Added ${size} marla size to all categories`, {
      variant: "success",
    });
  };

  const removeMarlaSize = (size) => {
    if (defaultMarlaSizes.includes(size)) {
      enqueueSnackbar("Cannot remove default marla sizes", {
        variant: "warning",
      });
      return;
    }

    setPricing((prev) => {
      const updated = { ...prev };
      categories.forEach((category) => {
        if (updated[category] && updated[category][size]) {
          delete updated[category][size];
        }
      });
      return updated;
    });

    enqueueSnackbar(`Removed ${size} marla size from all categories`, {
      variant: "success",
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const hasEmptyPrices = categories.some((category) =>
        marlaSizes.some(
          (size) =>
            !pricing[category]?.[size]?.price_per_marla ||
            pricing[category][size].price_per_marla <= 0
        )
      );

      if (hasEmptyPrices) {
        enqueueSnackbar(
          "Please set prices for all marla sizes and categories",
          { variant: "error" }
        );
        return;
      }

      const response = await updateSocietyPricing(society._id, pricing);

      if (response.updateSummary) {
        const { totalAvailablePlots, updatedPlotsCount, errorPlots } =
          response.updateSummary;

        if (updatedPlotsCount > 0) {
          enqueueSnackbar(
            `Pricing saved! Updated ${updatedPlotsCount} of ${totalAvailablePlots} available plots.`,
            { variant: "success" }
          );
        } else {
          enqueueSnackbar("Pricing configuration saved successfully!", {
            variant: "success",
          });
        }

        if (errorPlots.length > 0) {
          console.warn("Some plots could not be updated:", errorPlots);
          enqueueSnackbar(
            `${errorPlots.length} plots could not be updated. Check console for details.`,
            { variant: "warning" }
          );
        }
      } else {
        enqueueSnackbar("Pricing configuration saved successfully!", {
          variant: "success",
        });
      }

      const updatedSocieties = await dispatch(fetchSocieties(true)).unwrap();

      if (activeSociety && activeSociety._id === society._id) {
        const updatedSociety = updatedSocieties.find(
          (s) => s._id === society._id
        );
        if (updatedSociety) {
          await dispatch(updateSocietyInStore(updatedSociety));
          setActiveSociety(updatedSociety);

          setTimeout(() => {
            dispatch(fetchSocieties(true));
          }, 100);
        }
      }

      enqueueSnackbar(
        "Plot prices updated! The plots table will refresh automatically.",
        { variant: "success" }
      );

      if (onSave) {
        onSave(pricing);
      }
    } catch (error) {
      console.error("Error saving pricing:", error);
      enqueueSnackbar("Failed to save pricing configuration", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0";
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(amount)
      .replace("PKR", "")
      .trim();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-70 backdrop-blur-md flex items-center justify-center p-4">
      <div
        className={`${gradient} w-full max-w-7xl max-h-[96vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden`}
      >
        {/* Sleek Header */}
        <div className="relative bg-slate-900 border-b border-slate-700">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-slate-700/10"></div>
          <div className="relative px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Plot Pricing Configuration
                </h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  Set pricing for all plot categories and sizes
                </p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-600 text-gray-400 hover:text-white transition-all flex items-center justify-center"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-slate-900">
          <div className="p-6 space-y-6">
            {/* Add Custom Size Section */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="px-5 py-3 bg-slate-800/50 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-teal-400 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Custom Marla Size
                </h3>
              </div>
              <div className="p-5">
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={newMarlaSize}
                    onChange={(e) => setNewMarlaSize(e.target.value)}
                    placeholder="e.g., 7, 8, 15"
                    className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-all"
                    min="0.1"
                    step="0.1"
                  />
                  <button
                    onClick={addMarlaSize}
                    className="px-5 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Size
                  </button>
                </div>
              </div>
            </div>

            {/* Pricing Table */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="px-5 py-3 bg-slate-800/50 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-teal-400 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  Price Configuration Matrix
                </h3>
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-900/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 border-b border-slate-700">
                        Category
                      </th>
                      {marlaSizes.map((size) => (
                        <th
                          key={size}
                          className="px-4 py-3 text-center text-xs font-semibold text-gray-400 border-b border-slate-700 min-w-[140px]"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <span>{size} Marla</span>
                            {!defaultMarlaSizes.includes(size) && (
                              <button
                                onClick={() => removeMarlaSize(size)}
                                className="text-red-400 hover:text-red-300"
                                title="Remove size"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category, idx) => (
                      <tr
                        key={category}
                        className={
                          idx % 2 === 0 ? "bg-slate-900/30" : "bg-slate-900/50"
                        }
                      >
                        <td className="px-4 py-3 font-medium text-white border-b border-slate-700">
                          {category}
                        </td>
                        {marlaSizes.map((size) => (
                          <td
                            key={size}
                            className="px-4 py-3 border-b border-slate-700"
                          >
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                                PKR
                              </span>
                              <input
                                type="number"
                                value={
                                  pricing[category]?.[size]?.price_per_marla ||
                                  ""
                                }
                                onChange={(e) =>
                                  updatePricing(category, size, e.target.value)
                                }
                                placeholder="0"
                                className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-all"
                                min="0"
                              />
                            </div>
                            {pricing[category]?.[size]?.price_per_marla > 0 && (
                              <div className="mt-1.5 text-xs text-teal-400 text-center">
                                Total:{" "}
                                {formatCurrency(
                                  pricing[category][size].price_per_marla *
                                    parseFloat(size)
                                )}
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden p-4 space-y-4">
                {categories.map((category) => (
                  <div
                    key={category}
                    className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden"
                  >
                    <div className="px-4 py-2.5 bg-slate-800 border-b border-slate-700">
                      <h4 className="text-sm font-semibold text-white">
                        {category}
                      </h4>
                    </div>
                    <div className="p-3 space-y-3">
                      {marlaSizes.map((size) => (
                        <div key={size} className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-16 text-sm font-medium text-gray-400">
                            {size} Marla
                          </div>
                          <div className="flex-1 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                              PKR
                            </span>
                            <input
                              type="number"
                              value={
                                pricing[category]?.[size]?.price_per_marla || ""
                              }
                              onChange={(e) =>
                                updatePricing(category, size, e.target.value)
                              }
                              placeholder="0"
                              className="w-full pl-10 pr-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                              min="0"
                            />
                          </div>
                          {!defaultMarlaSizes.includes(size) && (
                            <button
                              onClick={() => removeMarlaSize(size)}
                              className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors flex items-center justify-center"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-1">
                      Available Plots
                    </h4>
                    <p className="text-xs text-gray-400">
                      Will be updated with new pricing
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-yellow-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-1">
                      Booked Plots
                    </h4>
                    <p className="text-xs text-gray-400">
                      Keep their original prices
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-teal-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-1">
                      Auto Calculate
                    </h4>
                    <p className="text-xs text-gray-400">
                      Total = Size × Price per marla
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-900 border-t border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <svg
                className="w-4 h-4 text-teal-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Set all prices before saving configuration</span>
            </div>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
                isLoading
                  ? "bg-gray-600 cursor-not-allowed opacity-50"
                  : "bg-teal-600 hover:bg-teal-500 shadow-lg hover:shadow-teal-500/20"
              } text-white`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving Configuration...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  Save Pricing Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingConfiguration;
